import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { XERO_CONNECT_KEY, XeroConnection, XeroService } from '../../services/xero.service';
import { formatDate } from '../../utils/dateUtils';

/** Xero tab: connection status, connect / reconnect, test, go-live date and disconnect. */
const XeroPanel: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution() || '';
    const [conn, setConn] = useState<XeroConnection | null>(null);
    const [goLive, setGoLive] = useState('');
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const show = (c: XeroConnection) => { setConn(c); setGoLive(c.goLiveDate ?? ''); };

    const load = useCallback(async () => {
        try {
            show(await XeroService.view(institutionId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the Xero connection');
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const connect = async () => {
        setBusy(true);
        try {
            const { authorizeUrl } = await XeroService.connect(institutionId);
            try {
                window.sessionStorage.setItem(XERO_CONNECT_KEY, JSON.stringify({ institutionId }));
            } catch {
                // Storage blocked: the callback page says to start again.
            }
            XeroService.redirect(authorizeUrl);
        } catch (e) {
            toast.showApiError(e, 'Could not start the Xero sign-in');
            setBusy(false);
        }
    };

    const run = async (action: () => Promise<XeroConnection>, done: (c: XeroConnection) => void, fail: string) => {
        setBusy(true);
        try {
            const c = await action();
            show(c);
            done(c);
        } catch (e) {
            toast.showApiError(e, fail);
        } finally {
            setBusy(false);
        }
    };

    const test = () => run(() => XeroService.test(institutionId),
        c => c.status === 'CONNECTED' ? toast.showSuccess('Xero accepted the connection') : toast.showError('Xero needs reconnecting'),
        'Could not test the connection');
    const saveGoLive = () => run(() => XeroService.setGoLive(institutionId, goLive),
        () => toast.showSuccess('Go-live date saved'), 'Could not save the go-live date');
    const disconnect = () => {
        setConfirmOpen(false);
        run(() => XeroService.disconnect(institutionId), () => toast.showSuccess('Disconnected from Xero'), 'Could not disconnect');
    };

    if (!conn) return null;

    if (!conn.serverReady) {
        return <Alert severity="info">Xero is not set up on this server yet. Ask your RunBizOps provider to switch it on.</Alert>;
    }
    if (!conn.addonOn) {
        return <Alert severity="info">The Xero add-on is off for this company.</Alert>;
    }

    const connected = conn.status === 'CONNECTED';
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Send this company's accounting documents to a Xero organisation. RunBizOps never sees your Xero password.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                {conn.status === 'NOT_CONNECTED' && (
                    <>
                        <Typography sx={{ mb: 2 }}>Not connected.</Typography>
                        <Button variant="contained" disabled={busy} onClick={connect} data-help="data.integrations.xero.connect">Connect to Xero</Button>
                    </>
                )}
                {conn.status === 'NEEDS_RECONNECT' && (
                    <>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Xero stopped accepting this connection{conn.orgName ? ` to "${conn.orgName}"` : ''}. Reconnect to keep sending.
                        </Alert>
                        <Button variant="contained" disabled={busy} onClick={connect} data-help="data.integrations.xero.reconnect">Reconnect</Button>
                    </>
                )}
                {connected && (
                    <>
                        <Typography sx={{ mb: 2 }}>
                            Connected to "{conn.orgName}"
                            {conn.connectedAt ? ` since ${formatDate(conn.connectedAt)}` : ''}
                            {conn.connectedByName ? ` by ${conn.connectedByName}` : ''}.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Button variant="outlined" disabled={busy} onClick={test} data-help="data.integrations.xero.test">Test connection</Button>
                            <Button color="error" variant="outlined" disabled={busy} onClick={() => setConfirmOpen(true)} data-help="data.integrations.xero.disconnect">Disconnect</Button>
                        </Box>
                    </>
                )}
            </Paper>
            {connected && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField
                        type="date" size="small" label="Go-live date" value={goLive} onChange={e => setGoLive(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 200px', maxWidth: 260 }} data-help="data.integrations.xero.goLive"
                    />
                    <Button variant="outlined" disabled={busy || !goLive} onClick={saveGoLive} data-help="data.integrations.xero.saveGoLive">Save</Button>
                </Box>
            )}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Disconnect from Xero?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Nothing more will be sent to Xero until someone connects again. Data already in Xero stays there.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={disconnect}>Yes, disconnect</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default XeroPanel;
